"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ChevronDown, ArrowRight, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import Image from "next/image";
import { brands } from "@/lib/constants/nav";
import { useSession } from "next-auth/react";

interface Category {
  label: string;
  href: string;
  submenu?: {
    label: string;
    href: string;
  }[];
}

interface ShopNavProps {
  disabled?: boolean;
}

export const ShopNav = ({ disabled }: ShopNavProps = {}) => {
  const { data: session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setSelectedCategory(null);
    }
  }, [isOpen]);

  const handleCategoryClick = (category: Category) => {
    if (category.submenu) {
      if (selectedCategory?.label === category.label) {
        setIsExpanded(false);
        setSelectedCategory(null);
      } else {
        if (selectedCategory) {
          setIsExpanded(false);
          setTimeout(() => {
            setSelectedCategory(category);
            setIsExpanded(true);
          }, 150);
        } else {
          setSelectedCategory(category);
          setIsExpanded(true);
        }
      }
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild disabled={disabled}>
        <Button
          variant='ghost'
          className={`px-4 mx-4 py-2 rounded-md text-sm flex items-center ${
            disabled
              ? "cursor-not-allowed text-muted-foreground"
              : "cursor-pointer hover:bg-primary"
          }`}
          disabled={disabled}
          onClick={disabled ? undefined : () => setIsOpen(true)}
        >
          Shop
          <ChevronDown
            className={`ml-2 h-4 w-4 ${disabled ? "text-muted-foreground" : ""}`}
          />
        </Button>
      </SheetTrigger>
      <SheetContent
        side='left'
        className={`flex flex-col p-0 gap-0 transition-all duration-300 !z-[9999] mt-[65px] ${
          isExpanded ? "!w-[800px]" : "!w-[400px]"
        }`}
      >
        <SheetTitle className='sr-only'>Shop Navigation Menu</SheetTitle>
        <SheetDescription className='sr-only'>
          Browse our product categories and collections for Rock Board® and
          XCELR8® brands
        </SheetDescription>
        <div className='flex flex-1 bg-background h-[100vh]'>
          <div
            className={`w-[400px] transition-all duration-300 relative z-20 bg-background ${
              isExpanded ? "border-r border-muted" : ""
            }`}
          >
            <ScrollArea className='h-full bg-background'>
              <div className='space-y-6 p-4 pb-28'>
                <div className='space-y-4'>
                  <div className='flex items-center justify-between h-9'>
                    <div className='relative w-[180px] h-[35px] ml-4'>
                      <Image
                        src='/Rock_Board_Word.png'
                        alt='Rock Board'
                        fill
                        className='object-contain'
                        sizes='180px'
                        priority
                      />
                    </div>
                    <SheetClose asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6 relative z-50 mr-2'
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </SheetClose>
                  </div>
                  <div className='space-y-1 pl-4'>
                    {brands.rockBoard.submenu.map((category) => (
                      <div key={category.label}>
                        {category.submenu ? (
                          <Button
                            variant='ghost'
                            className='w-full justify-between py-3 hover:bg-muted/50 relative'
                            onClick={() => handleCategoryClick(category)}
                          >
                            <span>{category.label}</span>
                            <ArrowRight className='h-4 w-4 relative z-10' />
                          </Button>
                        ) : (
                          <Link
                            href={category.href}
                            onClick={() => setIsOpen(false)}
                          >
                            <Button
                              variant='ghost'
                              className='w-full justify-start py-3 hover:bg-muted/50'
                            >
                              {category.label}
                            </Button>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className='space-y-4 pt-4 border-t border-muted'>
                  <div className='relative w-[180px] h-[35px]'>
                    <Image
                      src='/XCELR8_Logo_Long_White.png'
                      alt='XCELR8'
                      fill
                      className='object-contain'
                      sizes='180px'
                      priority
                    />
                  </div>
                  <div className='space-y-1 pl-4'>
                    {brands.xcelr8.submenu.map((category) => (
                      <Link
                        key={category.label}
                        href={category.href}
                        onClick={() => setIsOpen(false)}
                      >
                        <Button
                          variant='ghost'
                          className='w-full justify-start py-3 hover:bg-muted/50'
                        >
                          {category.label}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>

                {session?.user?.companyName && (
                  <div className='space-y-4 pt-4 pl-4 border-t border-muted'>
                    <div className=' bg-white rounded-lg py-2 w-[150px] flex items-center justify-center'>
                      {session.user.companyImage && (
                        <Image
                          src={session.user.companyImage}
                          alt={session.user.companyName}
                          width={80}
                          height={80}
                          className='object-contain'
                          priority
                        />
                      )}
                    </div>
                    <div className='space-y-1'>
                      <Link
                        href={`/my-fleethub?tab=company`}
                        onClick={() => setIsOpen(false)}
                      >
                        <Button
                          variant='ghost'
                          className='w-full justify-start py-3 hover:bg-muted/50'
                        >
                          Products
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <div
            className={`w-[280px] transition-all duration-300 z-10 ${
              isExpanded
                ? "translate-x-0"
                : "translate-x-[-380px] pointer-events-none"
            } absolute left-[380px] top-0 h-full bg-background border-r border-muted`}
          >
            {selectedCategory && selectedCategory.submenu && (
              <ScrollArea className='h-full'>
                <div className='p-4 pb-28'>
                  <div className='space-y-6'>
                    <div className='flex items-center justify-between pl-4 pr-2 h-9'>
                      <h3 className='text-lg font-semibold'>
                        {selectedCategory.label}
                      </h3>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-6 w-6'
                        onClick={() => handleCategoryClick(selectedCategory)}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                    <div className='flex flex-col space-y-4 pl-4'>
                      {selectedCategory.submenu.map((option) => (
                        <Link
                          key={option.label}
                          href={option.href}
                          onClick={() => setIsOpen(false)}
                        >
                          <div className='flex items-center gap-3'>
                            <div className='relative w-[40px] h-[40px]'>
                              <Image
                                src={`/${
                                  option.label === "Custom"
                                    ? "Custom-Icon.png"
                                    : "Modular-Icon.png"
                                }`}
                                alt={option.label}
                                fill
                                className='object-contain'
                                sizes='40px'
                                priority
                              />
                            </div>
                            <Button
                              variant='ghost'
                              className='flex-1 justify-start py-3 hover:bg-muted/50'
                            >
                              {option.label}
                            </Button>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
